import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import * as S from './paginated-data.styled';
import { SlideIndicators } from './slide-indicators/slide-indicators';

interface PaginatedDataProps<T> {
  data: T[];
  itemsPerSlide: number;
  interval?: number;
  renderItems: (items: T[]) => React.ReactNode;
}

export const PaginatedData = <T extends unknown>({
  data,
  itemsPerSlide,
  interval = 15_000,
  renderItems: renderItemsContainer,
}: PaginatedDataProps<T>) => {
  const currentSlideRef = useRef(-1);
  const [itemsToRender, setItemsToRender] = useState<T[]>([]);

  const totalSlides = useMemo(() => {
    if (!data) return 0;
    return Math.ceil(data.length / itemsPerSlide);
  }, [data, itemsPerSlide]);

  const updateSlides = useCallback(() => {
    if (!data || data.length === 0) {
      return setItemsToRender([]);
    }
    currentSlideRef.current = (currentSlideRef.current + 1) % totalSlides;
    const startIndex = currentSlideRef.current * itemsPerSlide;
    const newItems = data.slice(startIndex, startIndex + itemsPerSlide);
    setItemsToRender(newItems);
  }, [data, totalSlides, itemsPerSlide]);

  useEffect(() => {
    updateSlides();
    const intervalId = setInterval(updateSlides, interval);
    return () => clearInterval(intervalId);
  }, [data, totalSlides, interval]);

  return (
    <S.Wrapper>
      <S.Content key={currentSlideRef.current}>{renderItemsContainer(itemsToRender)}</S.Content>
      <SlideIndicators totalSlides={totalSlides} currentSlide={currentSlideRef.current} />
    </S.Wrapper>
  );
};
